import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, exhaustMap, map, of, switchMap, tap } from 'rxjs';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import * as FinancialSecurityActions from './financial-security-configuration.actions';
import {
  ACTIVATE_COVERAGE_FORMULA,
  ACTIVATE_FINANCIAL_SECURITY_RULE,
  CREATE_FINANCIAL_SECURITY_BANK,
  CREATE_FINANCIAL_SECURITY_RULE,
  CREATE_FINANCIAL_SECURITY_TYPE,
  DEACTIVATE_FINANCIAL_SECURITY_RULE,
  DELETE_FINANCIAL_SECURITY_BANK,
  DELETE_FINANCIAL_SECURITY_TYPE,
  GET_BANKING_INTEGRATIONS,
  GET_FINANCIAL_SECURITY_RULES,
  GET_FINANCIAL_SECURITY_TYPES,
  UPDATE_FINANCIAL_SECURITY_BANK,
  UPDATE_FINANCIAL_SECURITY_RULE,
  UPDATE_FINANCIAL_SECURITY_TYPE,
  GET_FOB_REFERENCES,
  CREATE_FOB_REFERENCE,
  UPDATE_FOB_REFERENCE,
  DELETE_FOB_REFERENCE,
  GET_COVERAGE_FORMULAS,
  CREATE_COVERAGE_FORMULA,
  UPDATE_COVERAGE_FORMULA,
  DELETE_COVERAGE_FORMULA,
  GET_PEGGED_COST_CATEGORIES,
  DELETE_PEGGED_COST_CATEGORIES,
  UPDATE_PEGGED_COST_CATEGORIES,
  CREATE_PEGGED_COST_CATEGORIES,
  CREATE_BANK_GUARANTEE_TYPE,
  UPDATE_BANK_GUARANTEE_TYPE,
  GET_BANK_GUARANTEE_TYPES,
  DELETE_BANK_GUARANTEE_TYPE,
} from '../graphql/financial-security-configuration.graphql';
import { FinancialSecurityTypesResponse } from '../responses/get-financial-security.response';
import { CreateFinancialSecurityTypeResponse } from '../responses/create-security-types.response';
import { UpdateFinancialSecurityTypeResponse } from '../responses/update-security-types.response';
import { DeleteFinancialSecurityTypeResponse } from '../responses/delete-security-type.response';
import { CreateFinancialSecurityRuleResponse } from '../responses/create-security-rules.response';
import { UpdateFinancialSecurityRuleResponse } from '../responses/update-security-rule.response';
import { FinancialSecurityRulesResponse } from '../responses/get-security-rules.response';
import {
  ActivateFinancialSecurityRuleResponse,
  DeactivateFinancialSecurityRuleResponse,
} from '../responses/get-security-rules.response';
import { LIST_CURRENCIES } from '@modules/settings/settings.graphql';
import { ListCurrenciesResponse } from '@modules/settings/types/currency';
import { ListBankConfigsResponse } from '../responses/banking-configs.response';
import { CreateBankConfigResponse } from '../responses/create-banking.response';
import { UpdateBankConfigResponse } from '../responses/update-banking-config.response';
import { DeleteBankConfigResponse } from '../responses/delete-banking.response';
import {
  GetFobReferencesResponse,
  CreateFobReferenceResponse,
  UpdateFobReferenceResponse,
  DeleteFobReferenceResponse,
} from '../responses/fob-references.response';
import {
  GetCoverageFormulasResponse,
  CreateCoverageFormulaResponse,
  UpdateCoverageFormulaResponse,
  DeleteCoverageFormulaResponse,
  ActivateCoverageFormulaResponse,
} from '../responses/coverage-formulas.response';
import {
  GetPeggedCostCategoriesResponse,
  CreatePeggedCostCategoryResponse,
  UpdatePeggedCostCategoryResponse,
  DeletePeggedCostCategoryResponse,
} from '../responses/pegged-cost-categories.response';
import {
  CreateBankGuaranteeTypeResponse,
  DeleteBankGuaranteeTypeResponse,
  ListBankGuaranteeTypesResponse,
  UpdateBankGuaranteeTypeResponse,
} from '../responses/bank-guarantee-types.response';

@Injectable({
  providedIn: 'root',
})
export class FinancialSecurityConfigurationEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  // Security Types Effects
  $saveSecurityTypeFormations = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecuritySubmitTypesAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreateFinancialSecurityTypeResponse>({
            mutation: CREATE_FINANCIAL_SECURITY_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createFinancialSecurityType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecuritySubmitTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecuritySubmitTypesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecuritySubmitTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateSecurityTypeFormations = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityTypesUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateFinancialSecurityTypeResponse>({
            mutation: UPDATE_FINANCIAL_SECURITY_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input, uuid: request.uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateFinancialSecurityType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityTypesUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityTypesUpdateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityTypesUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteSecurityType = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityTypesDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteFinancialSecurityTypeResponse>({
            mutation: DELETE_FINANCIAL_SECURITY_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteFinancialSecurityType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityTypesDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityTypesDeleteAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityTypesDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getSecurityTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityTypesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<FinancialSecurityTypesResponse>({
            query: GET_FINANCIAL_SECURITY_TYPES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.financialSecurityTypes;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityTypesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // Security Rules Effects would go here (similar structure to above effects)
  $saveSecurityRules = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecuritySubmitRulesAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreateFinancialSecurityRuleResponse>({
            mutation: CREATE_FINANCIAL_SECURITY_RULE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createFinancialSecurityRuleVersion;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecuritySubmitRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecuritySubmitRulesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecuritySubmitRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateSecurityRules = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityRulesUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateFinancialSecurityRuleResponse>({
            mutation: UPDATE_FINANCIAL_SECURITY_RULE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input, id: request.id },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateFinancialSecurityRule;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityRulesUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityRulesUpdateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteSecurityRule = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityRulesDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteFinancialSecurityTypeResponse>({
            mutation: DELETE_FINANCIAL_SECURITY_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteFinancialSecurityType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityRulesDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityRulesDeleteAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getSecurityRules = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityRulesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<FinancialSecurityRulesResponse>({
            query: GET_FINANCIAL_SECURITY_RULES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.financialSecurityRules;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityRulesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $activateSecurityRule = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityRulesActivateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<ActivateFinancialSecurityRuleResponse>({
            mutation: ACTIVATE_FINANCIAL_SECURITY_RULE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { id: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.activateFinancialSecurityRule;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityRulesActivateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Activation failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityRulesActivateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesActivateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deactivateSecurityRule = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeactivateFinancialSecurityRuleResponse>({
            mutation: DEACTIVATE_FINANCIAL_SECURITY_RULE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { id: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deactivateFinancialSecurityRule;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Deactivation failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // Currencies Effect
  $getCurrencies = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityCurrenciesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListCurrenciesResponse>({
            query: LIST_CURRENCIES,
            apolloNamespace: ApolloNamespace.settings,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listCurrencies;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityCurrenciesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityCurrenciesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  //Banking Configuration Effects
  $getBankingIntegrations = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListBankConfigsResponse>({
            query: GET_BANKING_INTEGRATIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankConfigs;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $saveBankIntegration = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankingIntegrationsSubmitAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreateBankConfigResponse>({
            mutation: CREATE_FINANCIAL_SECURITY_BANK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createBankConfig;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityBankingIntegrationsSubmitAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Registration failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityBankingIntegrationsSubmitAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecuritySubmitRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateBankIntegration = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateBankConfigResponse>({
            mutation: UPDATE_FINANCIAL_SECURITY_BANK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input, uuid: request.uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateBankConfig;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Registration failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteBankIntegration = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankingIntegrationsDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteBankConfigResponse>({
            mutation: DELETE_FINANCIAL_SECURITY_BANK,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteBankConfig;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityBankingIntegrationsDeleteAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Registration failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityBankingIntegrationsDeleteAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankingIntegrationsDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // Bank Guarantee Types Effects
  $getBankGuaranteeTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListBankGuaranteeTypesResponse>({
            query: GET_BANK_GUARANTEE_TYPES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankGuaranteeTypes;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $saveBankGuaranteeType = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreateBankGuaranteeTypeResponse>({
            mutation: CREATE_BANK_GUARANTEE_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createBankGuaranteeType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Registration failed',
                      data: null,
                    },
                  },
                );
              }

              return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateBankGuaranteeType = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateBankGuaranteeTypeResponse>({
            mutation: UPDATE_BANK_GUARANTEE_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input, uuid: request.uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateBankGuaranteeType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Registration failed',
                      data: null,
                    },
                  },
                );
              }

              return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteBankGuaranteeType = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteBankGuaranteeTypeResponse>({
            mutation: DELETE_BANK_GUARANTEE_TYPE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteBankGuaranteeType;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Deletion failed',
                      data: null,
                    },
                  },
                );
              }

              return FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // Countries Effect
  $getCoutries = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityCountriesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListCurrenciesResponse>({
            query: LIST_CURRENCIES,
            apolloNamespace: ApolloNamespace.settings,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listCurrencies;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityCurrenciesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityCurrenciesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityRulesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // FOB References Effects
  $saveFobReference = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreateFobReferenceResponse>({
            mutation: CREATE_FOB_REFERENCE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createFobReferenceValue;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Save failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateFobReference = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateFobReferenceResponse>({
            mutation: UPDATE_FOB_REFERENCE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request.uuid, input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateFobReferenceValue;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Update failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteFobReference = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteFobReferenceResponse>({
            mutation: DELETE_FOB_REFERENCE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteFobReferenceValue;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Delete failed',
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getFobReferences = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityFobReferencesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetFobReferencesResponse>({
            query: GET_FOB_REFERENCES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.fobReferenceValues;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityFobReferencesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityFobReferencesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityFobReferencesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // Coverage Formulas
  $saveCoverageFormula = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreateCoverageFormulaResponse>({
            mutation: CREATE_COVERAGE_FORMULA,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createFinancialCoverageFormulaVersion;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Save failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateCoverageFormula = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdateCoverageFormulaResponse>({
            mutation: UPDATE_COVERAGE_FORMULA,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request.uuid, input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updateFinancialCoverageFormula;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Update failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deleteCoverageFormula = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeleteCoverageFormulaResponse>({
            mutation: DELETE_COVERAGE_FORMULA,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteFinancialCoverageFormula;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Delete failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getCoverageFormulas = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetCoverageFormulasResponse>({
            query: GET_COVERAGE_FORMULAS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.financialCoverageFormulas;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $activateCoverageFormula = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<ActivateCoverageFormulaResponse>({
            mutation: ACTIVATE_COVERAGE_FORMULA,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.activateFinancialCoverageFormula;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Activation failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.success(
                {
                  response: {
                    success: true,
                    statusCode: 200,
                    message: payload.message,
                    data: payload,
                  },
                },
              );
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // Pegged Cost 
  $savePeggedCost = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<CreatePeggedCostCategoryResponse>({
            mutation: CREATE_PEGGED_COST_CATEGORIES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.createPeggedCostCategory;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Save failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updatePegedCost = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<UpdatePeggedCostCategoryResponse>({
            mutation: UPDATE_PEGGED_COST_CATEGORIES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request.uuid, input: request.input },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.updatePeggedCostCategory;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Update failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $deletePeggedCost = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.request),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<DeletePeggedCostCategoryResponse>({
            mutation: DELETE_PEGGED_COST_CATEGORIES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deletePeggedCostCategory;
              if (!payload?.status) {
                return FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.failure(
                  {
                    error: {
                      success: false,
                      statusCode: 400,
                      message: payload?.message ?? 'Delete failed',
                      data: null,
                    },
                  },
                );
              }
              return FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getPeggedCost = createEffect(() =>
    this.actions$.pipe(
      ofType(FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<GetPeggedCostCategoriesResponse>({
            query: GET_PEGGED_COST_CATEGORIES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.peggedCostCategories;
              if (!payload) {
                return FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
